import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import Loading from "../../Loading/Loading";
import { BiImages } from "react-icons/bi";
import { uploadCarouselImages } from "../../../Services/resources";
import { connect, useDispatch } from "react-redux";
import { themeColors } from "../../theme";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import "../../../theme-vars.css";
import Modal, { Styles } from "react-modal";


type props = {
  open: boolean;
  close: () => void;
  organization_id: string;
  history: any;
  usersList?: any[];
  theme: any;
};

const ImportModal: FunctionComponent<props> = ({
  open,
  close,
  organization_id,
  history,
  usersList,
  theme
}) => {
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /** Raw selected files */
  const [files, setFiles] = useState<FileList>();

  /** Base64 images for preview */
  const [images, setImages] = useState<string[]>([]);

  /** Indexes of selected images */
  const [selectedImages, setSelectedImages] = useState<number[]>([]);

  /** Loading state */
  const [load, setLoad] = useState(false);

  const dispatcher = useDispatch();

  /**
 * Helper: reads an image file as Base64 DataURL
 */
  const readImage = (file: any) => {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = (e) => {
        resolve(e.target?.result);
      };
      fr.readAsDataURL(file);
    });
  };

  /**
 * Handles upload of multiple images
 * Converts to Base64 for preview
 */
  const upload = async (data: FileList) => {
    const imageList: any[] = [];
    setFiles(data);
    for await (const result of Array.from(data).map(async (item) => {
      const image = await readImage(item);
      imageList.push(image);
      return true;
    })) {
      if (result) {
        setSelectedImages(Array.from(Array(imageList.length).keys()));
        setImages([...imageList]);
      }
    }
  };

  /**
 * Upload selected images to backend
 */
  const onSubmit = () => {
    setLoad(true);
    let data: any[] = [];

    // Only upload selected files
    selectedImages.forEach((item: any, index: number) => {
      files && data.push(files[item]);
    });
    uploadCarouselImages(
      data,
      organization_id,
      dispatcher,
      (data) => setLoad(data),
      close
    );
  };


  const containerStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }

  /**
 * Modal styling for dark/light mode
 */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "10px 20px",
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /** ------------------------------------------------------------------
    * Render Component
    * ------------------------------------------------------------------ */
  return (
    <>
      {load === true && <Loading />}
      <Modal
        isOpen={open}
        onRequestClose={close}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        style={modalStyle}
        ariaHideApp={false}
      >
        {load === true && <Loading />}


        {images.length !== 0 ? (
          <>
            <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)", paddingTop: isMobile ? "unset" : "15px" }}>Selected Images</h5>
            <div className={styles.imageContainer}>

              {/* Image Preview List */}
              {images.map((item: any, index: number) => (
                <div className={styles.imageBox} key={index}>
                  {/* Checkbox to toggle selection */}
                  <input
                    type="checkbox"
                    checked={selectedImages.includes(index)}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        let data = [...selectedImages];
                        let item = selectedImages.indexOf(index);
                        if (item > -1) {
                          data.splice(item, 1);
                        }
                        setSelectedImages(data);
                      } else {
                        let data = [...selectedImages];
                        data.push(index);
                        setSelectedImages(data);
                      }
                    }}
                  />
                  <img
                    src={item}
                    alt={"uploaded"}
                  // width and height can be omitted, CSS will handle sizing
                  />
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className={styles.buttonBox} style={{ marginTop: "2rem" }}>
              <StyledCancelButton
                onClick={close}
              >
                Cancel
              </StyledCancelButton>
              <StyledSubmitButton onClick={() => onSubmit()}>
                Confirm
              </StyledSubmitButton>
            </div>
          </>
        ) : (
          /** Initial Upload Screen */
          <div className={styles.child} style={{ height: isMobile ? "50vh" : "70vh" }}>
            <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-medium)", paddingTop: isMobile ? "unset" : "15px" }}>Import Data for "Image Form"</h5>
            {/* Drag and Drop Box */}
            <div
              className={styles.dragBox}
              style={containerStyles}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                upload(e.dataTransfer.files);
              }}
            >
              <BiImages size={100} color={"#808080"} />
              <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>Drag Your Images Here</p>
            </div>

            {/* Upload Button + Hidden Input */}
            <div className={styles.selectBox}>
              <div style={{ marginRight: 8 }}>
                <StyledCancelButton onClick={close}>
                  Cancel
                </StyledCancelButton>
              </div>
              <div>
                <input
                  id="file-input"
                  className={styles.select}
                  type="file"
                  multiple={true}
                  accept=".png,.jpg,.jpeg"
                  onChange={(e: any) => upload(e.target.files)}
                />
                <StyledSubmitButton
                  onClick={() => document.getElementById("file-input")?.click()}
                >
                  Upload File
                </StyledSubmitButton></div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

/** Map Redux store data to props */
const mapStateToProps = (state: any) => {
  return {
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(ImportModal);
